import { useState, useMemo } from 'react';
import { Flex, Text, Avatar, View, Button, IconButton, IconChatLine, Badge, Menu } from '@smarterservices/smarter-ui';
import ProctorsOnlineStack from './ProctorsOnlineStack';
import useAppStore from '../../stores/app';
import SessionManager from '../SessionManager';
/**
 * HeaderBar Component
 * 
 * Displays a horizontal menu bar at the top of the Virtual Test Center
 * that spans the entire page width.
 * 
 * @returns {JSX.Element} The header bar component
 */
const HeaderBar = () => {

    const [confirmSwitchRooms, setConfirmSwitchRooms] = useState(false);
    const [confirmLogout, setConfirmLogout] = useState(false);
    const [sessionManagerOpen, setSessionManagerOpen] = useState(false);

    const exitRoom = useAppStore((state) => state.exitRoom);
    const logout = useAppStore((state) => state.logout);
    const user = useAppStore((state) => state.user);
    const room = useAppStore((state) => state.room);
    const iam = useAppStore((state) => state.iam);
    const isRoomChatEnabled = useAppStore((state) => state.isRoomChatEnabled);
    const setRoomChatModalOpen = useAppStore((state) => state.setRoomChatModalOpen);
    const unreadRoomChatCount = useAppStore((state) => state.unreadRoomChatCount);
    const getIamResourceString = useAppStore((state) => state.getIamResourceString);

    const handleSwitchRoomsClick = (e) => {
        e.stopPropagation();
        setConfirmLogout(false);
        setConfirmSwitchRooms(true);
    };

    const handleLogoutClick = (e) => {
        e.stopPropagation();
        setConfirmSwitchRooms(false);
        setConfirmLogout(true);
    };

    const handleConfirmSwitchRooms = (e) => {
        e.stopPropagation();
        exitRoom();
    };

    const handleConfirmLogout = (e) => {
        e.stopPropagation();
        logout();
    };

    const handleMenuDismiss = () => {
        setConfirmSwitchRooms(false);
        setConfirmLogout(false);
    };

    const handleChatClick = () => {
        // Open chat in a new popup window, passing the token in query parameter
        const url = new URL('/vtc/windows/chat', window.location.origin);
        url.searchParams.set('token', user.token);
        window.open(url.toString(), 'vtc-chat', 'width=800,height=600,scrollbars=yes,resizable=yes');

        //        setRoomChatModalOpen(true);
    };

    return (
        <Flex.Item
            as="div"
            id="horizontal-menu-bar"
            height="40px"
            width="100%"
            background="primary-inverse"
            padding="small"
        >
            <Flex alignItems="center" height="100%" justifyItems="end">
                <Flex.Item>
                    <Text variant="contentSmall">{room?.roomName}</Text>
                </Flex.Item>
                <Flex.Item textAlign="end" shouldGrow padding="none medium none none">

                    {isRoomChatEnabled && (
                        <View margin="none none none small" display="inline-block" position="relative">
                            <Button
                                screenReaderLabel="Room Chat"
                                size="small"
                                variant="secondary"
                                onClick={handleChatClick}
                            >
                                <IconChatLine /> Chat
                                {unreadRoomChatCount > 0 && (
                                    <Badge
                                        count={unreadRoomChatCount}
                                        countUntil={99}
                                        placement="top end"
                                        standalone={false}
                                        type="notification"
                                    />
                                )}
                            </Button>
                        </View>
                    )}
                </Flex.Item>
                <Flex.Item>
                    {room && <ProctorsOnlineStack />}

                    <View margin="none none none small" borderWidth="none none none small" display="inline-block" position="relative">
                        <Menu
                            placement="bottom end"
                            withArrow={false}
                            shouldContainFocus
                            shouldReturnFocus
                            onDismiss={handleMenuDismiss}
                            themeOverride={{
                                minWidth: '150px',
                            }}
                            trigger={
                                <span style={{ display: 'inline-block' }}>
                                    <Avatar
                                        margin="none none none small"
                                        size="xx-small"
                                        name={user.firstName + ' ' + user.lastName}
                                        as="button"
                                    />
                                </span>
                            }
                        >
                            {room && (
                                <Menu.Item>
                                    <div onClick={handleSwitchRoomsClick} style={{ width: '100%', padding: '0.5rem', margin: '-0.5rem', cursor: 'pointer' }}>
                                        {confirmSwitchRooms ? (
                                            <Flex alignItems="center">
                                                <Flex.Item>Sure?</Flex.Item>
                                                <Flex.Item>
                                                    <Button margin="none none none small" size="small" color="danger" onClick={handleConfirmSwitchRooms}>Yes</Button>
                                                </Flex.Item>
                                            </Flex>
                                        ) : (
                                            'Switch Rooms'
                                        )}
                                    </div>
                                </Menu.Item>
                            )}
                            <Menu.Item>
                                <div onClick={handleLogoutClick} style={{ width: '100%', padding: '0.5rem', margin: '-0.5rem', cursor: 'pointer' }}>
                                    {confirmLogout ? (
                                        <Flex gap="small" alignItems="center">
                                            <Flex.Item>Sure?</Flex.Item>
                                            <Flex.Item>
                                                <Button margin="none none none small" size="small" color="danger" onClick={handleConfirmLogout}>Yes</Button>
                                            </Flex.Item>
                                        </Flex>
                                    ) : (
                                        'Logout'
                                    )}
                                </div>
                            </Menu.Item>
                            <Menu.Separator />
                            {room && (
                                <Menu.Item
                                    onSelect={() => setSessionManagerOpen(true)}
                                    checkPermissionsAnd={[
                                        { action: 'sp:ViewRoomSessionManager', resource: getIamResourceString(`room/${room?.sid}`) }
                                    ]}
                                >
                                    Session Manager
                                </Menu.Item>
                            )}
                            <Menu.Separator />
                            <Menu.Item disabled>
                                <Text size="x-small" color="secondary">
                                    {typeof __APP_VERSION__ !== 'undefined' ? `v${__APP_VERSION__}` : 'Version Unknown'}
                                    {typeof __APP_MODE__ !== 'undefined' && __APP_MODE__ !== 'production' && (
                                        <> ({__APP_MODE__})</>
                                    )}
                                </Text>
                            </Menu.Item>
                        </Menu>
                        <SessionManager
                            open={sessionManagerOpen}
                            onDismiss={() => setSessionManagerOpen(false)}
                        />
                    </View>
                </Flex.Item>

            </Flex>
        </Flex.Item>
    );
};

export default HeaderBar;
